'use client'

import React, { createContext, useCallback, useRef } from 'react'
import ReCAPTCHA, { type ReCAPTCHAHandle } from 'react-google-recaptcha'

// Define the context shape
export interface RecaptchaContextValue {
  getToken: (action: string) => Promise<string | null>
}

// Create and export the context
export const RecaptchaContext = createContext<RecaptchaContextValue | null>(null)

// A helper component that gets the real invisible v2 token function
// and provides it to the context.
const RecaptchaValueProvider = ({ children }: { children: React.ReactNode }) => {
  const recaptchaRef = useRef<ReCAPTCHAHandle | null>(null)

  const getToken = useCallback(
    async (_action: string) => {
      if (!recaptchaRef.current) {
        console.warn('reCAPTCHA not initialized')
        return null
      }
      try {
        const token = await recaptchaRef.current.executeAsync()
        return token
      } finally {
        recaptchaRef.current.reset()
      }
    },
    []
  )

  return (
    <RecaptchaContext.Provider value={{ getToken }}>
      <div className="sr-only" aria-hidden="true">
        <ReCAPTCHA
          ref={recaptchaRef}
          sitekey={process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY || ''}
          size="invisible"
        />
      </div>
      {children}
    </RecaptchaContext.Provider>
  )
}

// The main provider that decides whether to use the real or a mock implementation
export function RecaptchaProvider({ children }: { children: React.ReactNode }) {
  const recaptchaKey = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY

  if (!recaptchaKey) {
    console.warn('reCAPTCHA site key not found, reCAPTCHA will be disabled.')
    // Provide a mock implementation if the key is missing
    const mockGetToken = async (action: string) => {
      console.log(`Mock reCAPTCHA token requested for action: ${action}`)
      return null
    }
    return (
      <RecaptchaContext.Provider value={{ getToken: mockGetToken }}>
        {children}
      </RecaptchaContext.Provider>
    )
  }

  return (
    <RecaptchaValueProvider>{children}</RecaptchaValueProvider>
  )
}
